
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useEmailCheck } from '@/hooks/useEmailCheck';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Building, Briefcase } from 'lucide-react';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';
import { EmailAlert } from './EmailAlert';

// Fonction de debug conditionnelle
const debugLog = (message: string, data?: unknown) => {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log(message, data);
  }
};

interface SignUpFormProps {
  onSwitchToSignIn: () => void;
}

export const SignUpForm = ({ onSwitchToSignIn }: SignUpFormProps) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    company: '',
    position: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);

  const { signUp } = useAuth();
  const { toast } = useToast();
  const { checkEmailExists, getEmailCheckResult } = useEmailCheck();
  const navigate = useNavigate();

  // Validation d'un champ individuel
  const validateField = (field: string, value: string): string => {
    let error = '';
    
    switch(field) {
      case 'email':
        if (!value) {
          error = 'L\'email est requis';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'L\'email n\'est pas valide';
        }
        break;
      case 'password':
        if (!value) {
          error = 'Le mot de passe est requis';
        } else if (value.length < 8) {
          error = 'Le mot de passe doit contenir au moins 8 caractères';
        }
        break;
      case 'confirmPassword':
        if (!value) {
          error = 'La confirmation est requise';
        } else if (formData.password !== value) {
          error = 'Les mots de passe ne correspondent pas';
        }
        break;
      case 'firstName':
        if (!value) error = 'Le prénom est requis';
        break;
      case 'lastName':
        if (!value) error = 'Le nom est requis';
        break;
      case 'company':
        if (!value) error = 'L\'entreprise est requise';
        break;
      case 'position':
        if (!value) error = 'Le poste est requis';
        break;
    }
    
    return error;
  };

  // Validation complète du formulaire
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    } else {
      // Vérifier si l'email existe déjà
      const emailCheckResult = getEmailCheckResult(formData.email);
      if (emailCheckResult.exists) {
        newErrors.email = 'Cet email est déjà utilisé';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'La confirmation est requise';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!formData.firstName) newErrors.firstName = 'Le prénom est requis';
    if (!formData.lastName) newErrors.lastName = 'Le nom est requis';
    if (!formData.company) newErrors.company = 'L\'entreprise est requise';
    if (!formData.position) newErrors.position = 'Le poste est requis';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Empêcher les soumissions multiples
    if (isLoading) return;

    debugLog('SignUp: Début de la soumission', {
      userAgent: navigator.userAgent,
      touchSupport: 'ontouchstart' in window,
      formData: { ...formData, password: '[HIDDEN]' }
    });

    if (!validateForm()) {
      debugLog('SignUp: Échec de la validation', errors);
      return;
    }

    setIsLoading(true);

    try {
      debugLog('SignUp: Appel de l\'API signUp');
      
      const { error } = await signUp(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
        formData.company,
        formData.position
      );

      if (error) {
        console.error('SignUp: Erreur de l\'API', error);
        
        // Gérer les erreurs spécifiques de Supabase
        let errorTitle = "Erreur d'inscription";
        let errorDescription = error.message || "Une erreur s'est produite lors de l'inscription";
        
        // Erreur d'email déjà utilisé
        if (error.message?.toLowerCase().includes('already') || 
            error.message?.toLowerCase().includes('exists') ||
            error.message?.toLowerCase().includes('registered')) {
          errorTitle = "Compte déjà existant";
          errorDescription = "Un compte existe déjà avec cet email.";
          
          // Afficher l'erreur sous le champ email
          setErrors(prev => ({
            ...prev,
            email: 'Cet email est déjà utilisé'
          }));
          
          // Toast avec actions suggérées
          toast({
            title: errorTitle,
            description: (
              <div className="space-y-2">
                <p>Un compte existe déjà avec cet email.</p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={onSwitchToSignIn}
                    className="px-3 py-1.5 bg-white text-teal-600 rounded-md text-sm font-medium hover:bg-gray-50 border border-teal-600"
                  >
                    Se connecter
                  </button>
                </div>
              </div>
            ) as any,
            variant: "destructive",
            duration: 10000
          });
          
          return; // Sortir pour ne pas afficher le toast générique
        }
        
        // Erreur de mot de passe faible
        if (error.message?.includes('weak') || error.message?.includes('password')) {
          errorTitle = "Mot de passe trop faible";
          errorDescription = "Ce mot de passe est trop faible ou trop commun. Veuillez choisir un mot de passe plus sécurisé et unique.";
          
          // Afficher l'erreur directement sous le champ mot de passe
          setErrors(prev => ({
            ...prev,
            password: 'Ce mot de passe est trop faible. Choisissez-en un plus complexe et unique.'
          }));
        }
        
        toast({
          title: errorTitle,
          description: errorDescription,
          variant: "destructive",
          duration: 8000 // Augmenter la durée pour les erreurs
        });
      } else {
        debugLog('SignUp: Inscription réussie');
        
        // Afficher un message de succès détaillé
        toast({
          title: "✅ Inscription réussie !",
          description: "Un email de confirmation vous a été envoyé. Vérifiez votre boîte de réception (et vos spams) pour activer votre compte.",
          duration: 10000 // 10 secondes pour laisser le temps de lire
        });
        
        // Basculer vers le mode connexion après 2 secondes
        setTimeout(() => {
          onSwitchToSignIn();
        }, 2000);
      }
    } catch (error: unknown) {
      console.error('SignUp: Erreur inattendue', error);
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce pour la vérification d'email
  const [emailCheckTimeout, setEmailCheckTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Effacer l'erreur quand l'utilisateur commence à taper
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Vérification d'email avec debounce
    if (field === 'email') {
      // Annuler la vérification précédente
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }

      // Programmer une nouvelle vérification après 800ms
      const timeout = setTimeout(() => {
        if (value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          checkEmailExists(value);
        }
      }, 800);

      setEmailCheckTimeout(timeout);
    }
  };

  // Nettoyer le timeout au démontage du composant
  useEffect(() => {
    return () => {
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }
    };
  }, [emailCheckTimeout]);

  // Validation onBlur - quand l'utilisateur quitte le champ
  const handleBlur = (field: string) => {
    const value = formData[field as keyof typeof formData];
    const error = validateField(field, value);
    
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  return (
    <div className="w-full">
      {/* Logo */}
      <div className="flex items-center mb-6">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="hover:opacity-80 transition-opacity"
          aria-label="Retour à l'accueil"
        >
          <img
            src="/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"
            alt="Getproof Logo"
            className="h-8"
          />
        </button>
      </div>

      {/* Title */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 mb-1">
          Créer votre compte
        </h1>
        <p className="text-sm text-gray-600">
          Rejoignez Getproof pour automatiser vos prises de références
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="firstName" className="text-sm font-medium text-gray-700 mb-1 block">
              Prénom
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                onBlur={() => handleBlur('firstName')}
                placeholder="Votre prénom"
                className={`pl-9 h-10 border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${errors.firstName ? 'border-red-500' : ''}`}
                style={{ fontSize: '16px' }}
                autoComplete="given-name"
              />
            </div>
            {errors.firstName && <p className="text-xs text-red-500 mt-0.5">{errors.firstName}</p>}
          </div>
          <div>
            <Label htmlFor="lastName" className="text-sm font-medium text-gray-700 mb-1 block">
              Nom
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                onBlur={() => handleBlur('lastName')}
                placeholder="Votre nom"
                className={`pl-9 h-10 border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${errors.lastName ? 'border-red-500' : ''}`}
                style={{ fontSize: '16px' }}
                autoComplete="family-name"
              />
            </div>
            {errors.lastName && <p className="text-xs text-red-500 mt-0.5">{errors.lastName}</p>}
          </div>
        </div>

        <div>
          <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-1 block">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              placeholder="votre@email.com"
              className={`pl-9 h-10 border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${
                errors.email || getEmailCheckResult(formData.email).exists ? 'border-red-500' : ''
              }`}
              style={{ fontSize: '16px' }}
              autoComplete="email"
            />
          </div>
          {errors.email && <p className="text-xs text-red-500 mt-0.5">{errors.email}</p>}
          
          {/* Alerte de vérification d'email - affiche uniquement si l'email existe */}
          {formData.email && (
            <EmailAlert
              email={formData.email}
              exists={getEmailCheckResult(formData.email).exists}
              isChecking={getEmailCheckResult(formData.email).isChecking}
              error={getEmailCheckResult(formData.email).error}
              onSwitchToSignIn={onSwitchToSignIn}
            />
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="company" className="text-sm font-medium text-gray-700 mb-1 block">
              Entreprise
            </Label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                id="company"
                type="text"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                onBlur={() => handleBlur('company')}
                placeholder="Votre entreprise"
                className={`pl-9 h-10 border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${errors.company ? 'border-red-500' : ''}`}
                style={{ fontSize: '16px' }}
                autoComplete="organization"
              />
            </div>
            {errors.company && <p className="text-xs text-red-500 mt-0.5">{errors.company}</p>}
          </div>
          <div>
            <Label htmlFor="position" className="text-sm font-medium text-gray-700 mb-1 block">
              Poste
            </Label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                id="position"
                type="text"
                value={formData.position}
                onChange={(e) => handleInputChange('position', e.target.value)}
                onBlur={() => handleBlur('position')}
                placeholder="Votre poste"
                className={`pl-9 h-10 border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${errors.position ? 'border-red-500' : ''}`}
                style={{ fontSize: '16px' }}
                autoComplete="organization-title"
              />
            </div>
            {errors.position && <p className="text-xs text-red-500 mt-0.5">{errors.position}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-1 block">
              Mot de passe
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                onFocus={() => setShowPasswordStrength(true)}
                onBlur={() => {
                  handleBlur('password');
                  setShowPasswordStrength(false);
                }}
                placeholder="Mot de passe"
                className={`pl-9 pr-9 h-10 border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${errors.password ? 'border-red-500' : ''}`}
                style={{ fontSize: '16px' }}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-red-500 mt-0.5">{errors.password}</p>}
            <PasswordStrengthIndicator 
              password={formData.password} 
              show={showPasswordStrength}
            />
          </div>
          <div>
            <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 mb-1 block">
              Confirmation
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                onBlur={() => handleBlur('confirmPassword')}
                placeholder="Confirmez"
                className={`pl-9 pr-9 h-10 border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                style={{ fontSize: '16px' }}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-xs text-red-500 mt-0.5">{errors.confirmPassword}</p>}
          </div>
        </div>

        <Button
          type="submit"
          className="w-full h-10 bg-teal-500 hover:bg-teal-600 active:bg-teal-700 text-white font-medium rounded-lg touch-manipulation"
          disabled={isLoading}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          {isLoading ? 'Inscription...' : 'S\'inscrire'}
        </Button>

        <div className="text-center pt-2">
          <span className="text-sm text-gray-600">Déjà un compte ? </span>
          <button
            type="button"
            onClick={onSwitchToSignIn}
            className="text-sm text-teal-600 hover:text-teal-700 hover:underline font-medium"
          >
            Se connecter
          </button>
        </div>
      </form>

      {/* Footer links */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex justify-center space-x-6 text-xs text-gray-500">
          <a href="#" className="hover:text-gray-700">Conditions générales</a>
          <a href="#" className="hover:text-gray-700">Politique de confidentialité</a>
        </div>
      </div>
    </div>
  );
};
